import PageTop from "@/components/PageTop";
import { trackSignUp } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { LineChart, PlayCircle, Flame, Filter, Users } from "lucide-react";
import { ElementType, ReactNode } from "react";

const features = [
  { label: "analytics", Icon: LineChart, color: "text-feature-web" },
  { label: "replays", Icon: PlayCircle, color: "text-feature-replays" },
  { label: "heatmaps", Icon: Flame, color: "text-feature-heatmaps" },
  { label: "funnels", Icon: Filter, color: "text-feature-funnels" },
  { label: "profiles", Icon: Users, color: "text-feature-profiles" },
];

interface HomeHeroProps {
  headline?: ReactNode;
  description?: ReactNode;
  showCta?: boolean;
  as?: ElementType;
  padBottom?: boolean;
  className?: string;
  icon?: ReactNode;
  primaryCta?: { label: string; href: string } | null;
  secondaryCta?: { label: string; href: string } | null;
}

const HomeHero = ({
  headline = "See what people do on your website",
  description = (
    <>
      Simple{" "}
      {features.map((f, i) => (
        <span key={f.label}>
          <span className="whitespace-nowrap">
            <f.Icon
              className={`inline-block align-[-0.2em] mr-1 h-4 w-4 md:h-5 md:w-5 ${f.color}`}
              aria-hidden
            />
            {f.label}
          </span>
          {i < features.length - 2
            ? ", "
            : i === features.length - 2
              ? ", and "
              : ""}
        </span>
      ))}{" "}
      — one platform to understand every visitor.
    </>
  ),
  showCta = true,
  as,
  padBottom = true,
  className,
  icon,
  primaryCta = { label: "Start free", href: "https://eu.bigdelta.com/sign-up" },
  secondaryCta = null,
}: HomeHeroProps) => {
  return (
    <PageTop
      as={as}
      padBottom={padBottom}
      className={cn("relative overflow-hidden", className)}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 hidden md:block w-[60%] -z-10"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 75% 40%, hsl(var(--foreground) / 0.10) 0%, hsl(var(--foreground) / 0.04) 35%, transparent 70%)",
          }}
        />
        <div
          className="absolute -top-24 -right-24 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-40"
          style={{
            background:
              "conic-gradient(from 200deg at 50% 50%, hsl(var(--foreground) / 0.10), transparent 40%, hsl(var(--foreground) / 0.06) 70%, transparent)",
          }}
        />
        <svg
          className="absolute inset-0 h-full w-full opacity-[0.07]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="hero-grid" width="44" height="44" patternUnits="userSpaceOnUse">
              <path d="M44 0H0V44" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
            <radialGradient id="hero-grid-fade" cx="70%" cy="40%" r="60%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
            <mask id="hero-grid-mask">
              <rect width="100%" height="100%" fill="url(#hero-grid-fade)" />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#hero-grid)"
            mask="url(#hero-grid-mask)"
            className="text-foreground"
          />
        </svg>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-background" />
      </div>

      <div className="relative max-w-4xl mx-auto flex flex-col items-center text-center gap-8 md:gap-10">
        <h1
          className="inline-flex items-center gap-3 sm:gap-4 text-4xl sm:text-5xl md:text-6xl leading-[1.05] md:leading-[1.1] tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {icon}
          {headline}
        </h1>
        <p className="text-lg md:text-xl leading-relaxed text-foreground max-w-md">
          {description}
        </p>
        {showCta && (
          <div className="flex flex-wrap items-center justify-center gap-3">
            {primaryCta && (
              <a
                href={primaryCta.href}
                onClick={trackSignUp}
                className="bg-foreground text-background px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground/90 transition-colors"
              >
                {primaryCta.label}
              </a>
            )}
            {secondaryCta && (
              <a
                href={secondaryCta.href}
                className="px-6 py-3 rounded-full text-sm font-medium border border-foreground/20 hover:border-foreground/40 transition-colors"
              >
                {secondaryCta.label}
              </a>
            )}
          </div>
        )}
      </div>
    </PageTop>
  );
};

export default HomeHero;
